/*
  File: src/pages/ProductManagement.jsx
  Purpose: Modern product management page for creating products with variants, players, and categories
  Features: Multi-step form, variant management, player/category selection, image upload, validation
*/
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Loader from '../components/common/Loader';
import { apiClient } from '../auth/api';

// Custom hook for fetching all form data in one request
function useFormData() {
  const [formData, setFormData] = useState({
    vendors: [],
    categories: [],
    players: [],
    characters: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchFormData = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await apiClient.get('/form-data/all');

        if (!cancelled) {
          // Process vendors
          const vendors = (data.vendors?.items || []).map(v => ({
            id: v.id,
            name: v.name
          }));

          // Process categories
          const categories = (data.categories?.items || []).map(c => ({
            id: c.id,
            name: c.display_name || c.name,
            level: c.level || 0,
            parent_id: c.parent_id || null
          }));

          // Process players
          const players = (data.players?.items || []).map(p => ({
            id: p.id,
            name: p.display_name,
            team: p.team || '',
            position: p.position || ''
          }));

          // Process characters
          const characters = (data.characters?.items || []).map(ch => ({
            id: ch.id,
            name: ch.display_name || ch.name,
            image_url: ch.image_url || '',
            series_id: ch.series_id || null
          }));

          setFormData({
            vendors,
            categories,
            players,
            characters
          });
        }
      } catch (error) {
        console.error('Failed to fetch form data:', error);
        if (!cancelled) {
          setError(error);
          // Set empty arrays as fallback
          setFormData({
            vendors: [],
            categories: [],
            players: [],
            characters: []
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFormData();
    return () => { cancelled = true; };
  }, []);

  return {
    vendors: formData.vendors,
    categories: formData.categories,
    players: formData.players,
    characters: formData.characters,
    loading,
    error
  };
}

// Utility functions
const formatPrice = (cents, currency = 'EUR') => {
  const symbol = currency === 'EUR' ? '€' : '$';
  return `${symbol}${(cents / 100).toFixed(2)}`;
};

const parsePriceInput = (value) => {
  const numValue = parseFloat(value.replace(/[^\d.]/g, ''));
  return isNaN(numValue) ? 0 : Math.round(numValue * 100);
};

const generateVariantSku = (baseSku, size, color) => {
  const sizeCode = size ? `-${size.toUpperCase()}` : '';
  const colorCode = color ? `-${color.toUpperCase().replace(/\s+/g, '')}` : '';
  return `${baseSku}${sizeCode}${colorCode}`;
};

// Form validation
const validateForm = (formData) => {
  const errors = {};

  if (!formData.vendor_id) errors.vendor_id = 'Vendor is required';
  if (!formData.sku?.trim()) errors.sku = 'SKU is required';
  if (!formData.name?.trim()) errors.name = 'Product name is required';
  if (!formData.display_name?.trim()) errors.display_name = 'Display name is required';
  if (!formData.description?.trim()) errors.description = 'Description is required';
  if (!formData.price_cents || formData.price_cents <= 0) errors.price_cents = 'Valid price is required';
  if (!formData.currency?.trim()) errors.currency = 'Currency is required';
  if (formData.variants.length === 0) errors.variants = 'At least one variant is required';

  // Validate variants
  formData.variants.forEach((variant, index) => {
    if (!variant.size?.trim()) {
      errors[`variant_${index}_size`] = 'Size is required';
    }
    if (!variant.color?.trim()) {
      errors[`variant_${index}_color`] = 'Color is required';
    }
    if (!variant.material?.trim()) {
      errors[`variant_${index}_material`] = 'Material is required';
    }
    if (!variant.stock || variant.stock < 0) {
      errors[`variant_${index}_stock`] = 'Valid stock quantity is required';
    }
  });

  return errors;
};

// Searchable single-select component
function SearchSelect({ options, value, onChange, placeholder, displayKey = 'name', valueKey = 'id' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const safeOptions = Array.isArray(options) ? options : [];

  const selectedOption = safeOptions.find(opt => String(opt[valueKey]) === String(value)) || null;

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return safeOptions;
    return safeOptions.filter(opt => String(opt[displayKey] || '').toLowerCase().includes(term));
  }, [safeOptions, searchTerm, displayKey]);

  return (
    <div className="relative">
      <div
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer min-h-[42px] flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="truncate text-white">
          {selectedOption ? selectedOption[displayKey] : <span className="text-white/50">{placeholder}</span>}
        </div>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg">
          <div className="p-2 border-b border-white/10">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.map(opt => (
              <div
                key={opt[valueKey]}
                className={`px-3 py-2 cursor-pointer hover:bg-white/10 ${String(opt[valueKey]) === String(value) ? 'bg-cyan-600/20 text-cyan-300' : ''}`}
                onClick={() => { onChange(opt[valueKey]); setIsOpen(false); setSearchTerm(''); }}
              >
                {opt[displayKey]}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-sm text-white/60">No options available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Component for hierarchical category selection
function CategoryMultiSelect({ categories, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Organize categories into parent-child structure
  const organizedCategories = useMemo(() => {
    const safeCategories = Array.isArray(categories) ? categories : [];
    const parentCategories = safeCategories.filter(cat => !cat.parent_id);
    const childCategories = safeCategories.filter(cat => cat.parent_id);

    return parentCategories.map(parent => ({
      ...parent,
      subcategories: childCategories.filter(child => child.parent_id === parent.id)
    }));
  }, [categories]);

  const toggleCategory = (category) => {
    const isSelected = selected.some(item => item.id === category.id);
    if (isSelected) {
      onChange(selected.filter(item => item.id !== category.id));
    } else {
      onChange([...selected, category]);
    }
  };

  const toggleSubcategory = (subcategory, parentCategory) => {
    const isSelected = selected.some(item => item.id === subcategory.id);
    if (isSelected) {
      onChange(selected.filter(item => item.id !== subcategory.id));
    } else {
      onChange([...selected, { ...subcategory, parent: parentCategory.name }]);
    }
  };

  const filteredTree = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return organizedCategories;
    return organizedCategories
      .map(parent => {
        const parentMatches = String(parent.name).toLowerCase().includes(term);
        const subs = (parent.subcategories || []).filter(sc => String(sc.name).toLowerCase().includes(term));
        if (parentMatches || subs.length > 0) {
          return { ...parent, subcategories: subs };
        }
        return null;
      })
      .filter(Boolean);
  }, [organizedCategories, searchTerm]);

  return (
    <div className="relative">
      <div
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer min-h-[42px] flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1">
          {selected.length === 0 ? (
            <span className="text-white/50">Select categories</span>
          ) : (
            selected.map(item => (
              <span key={item.id} className="bg-purple-600/20 text-purple-300 px-2 py-1 rounded text-sm">
                {item.parent ? `${item.parent} > ${item.name}` : item.name}
              </span>
            ))
          )}
        </div>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          <div className="p-2 border-b border-white/10 sticky top-0 bg-gray-800 z-10">
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {filteredTree.map(category => (
            <div key={category.id}>
              <div
                className={`px-3 py-2 cursor-pointer hover:bg-white/10 font-medium ${selected.some(item => item.id === category.id) ? 'bg-purple-600/20 text-purple-300' : ''}`}
                onClick={() => toggleCategory(category)}
              >
                {category.name}
              </div>
              {category.subcategories && category.subcategories.map(subcategory => (
                <div
                  key={subcategory.id}
                  className={`px-6 py-2 cursor-pointer hover:bg-white/10 text-sm ${selected.some(item => item.id === subcategory.id) ? 'bg-purple-600/20 text-purple-300' : 'text-white/70'}`}
                  onClick={() => toggleSubcategory(subcategory, category)}
                >
                  {subcategory.name}
                </div>
              ))}
            </div>
          ))}
          {filteredTree.length === 0 && (
            <div className="px-3 py-2 text-sm text-white/60">No categories available</div>
          )}
        </div>
      )}
    </div>
  );
}

// Component for image upload with drag and drop
function ImageUpload({ onImageChange, preview, error }) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      // Client-side size guard: 50KB max
      if (file.size > 50 * 1024) {
        onImageChange(null, 'Image too large. Max size is 50KB');
        return;
      }
      onImageChange(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Client-side size guard: 50KB max
      if (file.size > 50 * 1024) {
        onImageChange(null, 'Image too large. Max size is 50KB');
        return;
      }
      onImageChange(file);
    }
  };

  return (
    <div className="space-y-2">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging
          ? 'border-cyan-400 bg-cyan-400/10'
          : error
            ? 'border-red-400 bg-red-400/10'
            : 'border-white/20 hover:border-white/40'
          }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {preview ? (
          <div className="space-y-2">
            <img src={preview} alt="Preview" className="mx-auto max-h-32 rounded" />
            <p className="text-sm text-white/70">Click or drag to change image</p>
          </div>
        ) : (
          <div className="space-y-2">
            <svg className="mx-auto h-12 w-12 text-white/40" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="text-white/70">Drag and drop an image here, or click to select</p>
            <p className="text-sm text-white/50">PNG, JPG, GIF up to 10MB</p>
          </div>
        )}
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
    </div>
  );
}

// Add this helper above ProductManagement
function splitVariantBySizes(variant, baseSku, existingVariants) {
  if (!variant.size || !variant.size.includes(',')) return null;
  const sizes = variant.size.split(',').map(s => s.trim()).filter(Boolean);
  // Prevent duplicate SKUs
  const usedSkus = new Set(existingVariants.map(v => v.sku));
  return sizes.map(size => {
    const sku = generateVariantSku(baseSku, size, variant.color);
    let uniqueSku = sku;
    let i = 1;
    while (usedSkus.has(uniqueSku)) {
      uniqueSku = sku + '-' + i;
      i++;
    }
    usedSkus.add(uniqueSku);
    return {
      ...variant,
      size,
      sku: uniqueSku,
    };
  });
}

// Searchable multi-select (same behavior as previous MultiSelect)
function SearchMultiSelect({ options, selected, onChange, placeholder, searchKey = 'name', displayKey = 'name' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => {
    const safeOptions = Array.isArray(options) ? options : [];
    if (!searchTerm) return safeOptions;
    return safeOptions.filter(option =>
      String(option[searchKey] || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, searchKey]);

  const toggleOption = (option) => {
    const isSelected = selected.some(item => item.id === option.id);
    if (isSelected) {
      onChange(selected.filter(item => item.id !== option.id));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative">
      <div
        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer min-h-[42px] flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1">
          {selected.length === 0 ? (
            <span className="text-white/50">{placeholder}</span>
          ) : (
            selected.map(item => (
              <span key={item.id} className="bg-cyan-600/20 text-cyan-300 px-2 py-1 rounded text-sm">
                {item[displayKey]}
              </span>
            ))
          )}
        </div>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.map(option => (
              <div
                key={option.id}
                className={`px-3 py-2 cursor-pointer hover:bg-white/10 ${selected.some(item => item.id === option.id) ? 'bg-cyan-600/20 text-cyan-300' : ''}`}
                onClick={() => toggleOption(option)}
              >
                {option[displayKey]}
                {option.team && <span className="text-white/50 text-sm ml-2">({option.team})</span>}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-sm text-white/60">No options available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductManagement() {
  const { vendors, categories, players, characters, loading, error } = useFormData();

  // Entry mode: choose between creating a product or assigning an SKU first
  const [entryMode, setEntryMode] = useState('choose'); // 'choose' | 'create'
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Form state
  const [formData, setFormData] = useState({
    vendor_id: '',
    sku: '',
    name: '',
    display_name: '',
    description: '',
    product_url: '',
    price_cents: 0,
    currency: 'EUR',
    variants: [{
      sku: '',
      size: '',
      color: '',
      material: '',
      price_cents: 0,
      currency: 'EUR',
      stock: 0,
      images: []
    }],
    player_ids: [],
    character_ids: [],
    category_ids: []
  });

  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [associationMode, setAssociationMode] = useState('players'); // 'players' | 'characters'
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  // Assign SKU modal state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignItems, setAssignItems] = useState([]);
  const [assignFilter, setAssignFilter] = useState('');
  const [skuEdits, setSkuEdits] = useState({});
  const [savingProductId, setSavingProductId] = useState(null);

  // Assign Player modal state
  const [assignPlayerOpen, setAssignPlayerOpen] = useState(false);
  const [assignPlayerLoading, setAssignPlayerLoading] = useState(false);
  const [assignPlayerError, setAssignPlayerError] = useState('');
  const [assignPlayerItems, setAssignPlayerItems] = useState([]);
  const [assignPlayerFilter, setAssignPlayerFilter] = useState('');
  const [selectedPlayersForAssign, setSelectedPlayersForAssign] = useState({}); // Changed to store arrays
  const [assigningPlayerId, setAssigningPlayerId] = useState(null);
  const [assignPlayerSuccess, setAssignPlayerSuccess] = useState('');
  const [assignPlayerErrorMsg, setAssignPlayerErrorMsg] = useState('');

  // Assign Character modal state
  const [assignCharacterOpen, setAssignCharacterOpen] = useState(false);
  const [assignCharacterLoading, setAssignCharacterLoading] = useState(false);
  const [assignCharacterError, setAssignCharacterError] = useState('');
  const [assignCharacterItems, setAssignCharacterItems] = useState([]);
  const [assignCharacterFilter, setAssignCharacterFilter] = useState('');
  const [selectedCharactersForAssign, setSelectedCharactersForAssign] = useState({}); // Changed to store arrays
  const [assigningCharacterId, setAssigningCharacterId] = useState(null);
  const [assignCharacterSuccess, setAssignCharacterSuccess] = useState('');
  const [assignCharacterErrorMsg, setAssignCharacterErrorMsg] = useState('');

  // Step validation
  const validateStep = (step) => {
    const stepErrors = {};

    if (step === 1) {
      // Product Details & Image validation
      if (!formData.vendor_id) stepErrors.vendor_id = 'Vendor is required';
      if (!formData.sku?.trim()) stepErrors.sku = 'SKU is required';
      if (!formData.name?.trim()) stepErrors.name = 'Product name is required';
      if (!formData.display_name?.trim()) stepErrors.display_name = 'Display name is required';
      if (!formData.description?.trim()) stepErrors.description = 'Description is required';
      if (!formData.price_cents || formData.price_cents <= 0) stepErrors.price_cents = 'Valid price is required';
      if (!formData.currency?.trim()) stepErrors.currency = 'Currency is required';
    } else if (step === 2) {
      // Players & Categories validation (optional but could add requirements)
      // Currently no required validations for this step
    } else if (step === 3) {
      // Variants validation
      if (formData.variants.length === 0) stepErrors.variants = 'At least one variant is required';
      formData.variants.forEach((variant, index) => {
        if (!variant.size?.trim()) {
          stepErrors[`variant_${index}_size`] = 'Size is required';
        }
        if (!variant.color?.trim()) {
          stepErrors[`variant_${index}_color`] = 'Color is required';
        }
        if (!variant.material?.trim()) {
          stepErrors[`variant_${index}_material`] = 'Material is required';
        }
        if (!variant.stock || variant.stock < 0) {
          stepErrors[`variant_${index}_stock`] = 'Valid stock quantity is required';
        }
      });
    }

    return stepErrors;
  };

  const nextStep = () => {
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    setErrors({});
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const getStepTitle = (step) => {
    switch (step) {
      case 1: return 'Product Details & Image';
      case 2: return 'Players & Categories';
      case 3: return 'Product Variants';
      default: return 'Step';
    }
  };

  // Form handlers
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    if (field === 'images') {
      newVariants[index] = { ...newVariants[index], images: value };
    } else {
      newVariants[index] = { ...newVariants[index], [field]: value };
      // Auto-generate SKU for variant
      if (field === 'size' || field === 'color') {
        const variant = newVariants[index];
        newVariants[index].sku = generateVariantSku(formData.sku, variant.size, variant.color);
      }
    }
    setFormData(prev => ({ ...prev, variants: newVariants }));
    // Clear variant-specific errors
    const errorKey = `variant_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: null }));
    }
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, {
        sku: '',
        size: '',
        color: '',
        material: '',
        price_cents: prev.price_cents,
        currency: prev.currency,
        stock: 0,
        images: []
      }]
    }));
  };

  const removeVariant = (index) => {
    if (formData.variants.length > 1) {
      setFormData(prev => ({
        ...prev,
        variants: prev.variants.filter((_, i) => i !== index)
      }));
    }
  };

  const handleImageChange = (file, errorMessage) => {
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
    // Set error if provided
    if (errorMessage) {
      setErrors(prev => ({ ...prev, image: errorMessage }));
    } else {
      setErrors(prev => ({ ...prev, image: null }));
    }
  };

  const handlePlayersChange = (players) => {
    setSelectedPlayers(players);
    setFormData(prev => ({ ...prev, player_ids: players.map(p => p.id) }));
  };

  const handleCategoriesChange = (categories) => {
    setSelectedCategories(categories);
    setFormData(prev => ({ ...prev, category_ids: categories.map(c => c.id) }));
  };

  const handleCharactersChange = (charactersSel) => {
    setSelectedCharacters(charactersSel);
    setFormData(prev => ({ ...prev, character_ids: charactersSel.map(ch => ch.id) }));
  };

  const handlePriceChange = (value, isVariant = false, variantIndex = null) => {
    const cents = parsePriceInput(value);
    if (isVariant && variantIndex !== null) {
      updateVariant(variantIndex, 'price_cents', cents);
    } else {
      updateFormData('price_cents', cents);
      // Update all variants to match product price by default
      const newVariants = formData.variants.map(v => ({ ...v, price_cents: cents }));
      setFormData(prev => ({ ...prev, variants: newVariants }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only allow submission if on the final step
    if (currentStep !== totalSteps) {
      return; // Don't submit if not on final step
    }

    // Comprehensive validation for all steps
    const allErrors = {};

    // Step 1 validation
    if (!formData.vendor_id) allErrors.vendor_id = 'Vendor is required';
    if (!formData.name?.trim()) allErrors.name = 'Name is required';
    if (!formData.sku?.trim()) allErrors.sku = 'SKU is required';
    if (!formData.display_name?.trim()) allErrors.display_name = 'Display name is required';
    if (!formData.description?.trim()) allErrors.description = 'Description is required';
    if (!formData.price_cents || formData.price_cents <= 0) allErrors.price_cents = 'Valid price is required';

    // Step 3 validation (variants)
    if (!formData.variants || formData.variants.length === 0) {
      allErrors.variants = 'At least one variant is required';
    } else {
      formData.variants.forEach((variant, index) => {
        if (!variant.size?.trim()) allErrors[`variant_${index}_size`] = 'Size is required';
        if (!variant.color?.trim()) allErrors[`variant_${index}_color`] = 'Color is required';
        if (!variant.material?.trim()) allErrors[`variant_${index}_material`] = 'Material is required';
        if (!variant.stock || variant.stock < 0) allErrors[`variant_${index}_stock`] = 'Valid stock is required';
      });
    }

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      // Go back to the first step with errors
      if (allErrors.vendor_id || allErrors.name || allErrors.sku || allErrors.display_name || allErrors.description || allErrors.price_cents) {
        setCurrentStep(1);
      } else if (Object.keys(allErrors).some(key => key.startsWith('variant_'))) {
        setCurrentStep(3);
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare form data for multipart upload
      const submitData = new FormData();

      // Add basic fields (convert numbers to strings)
      submitData.append('vendor_id', formData.vendor_id.toString());
      submitData.append('sku', formData.sku);
      submitData.append('name', formData.name);
      submitData.append('display_name', formData.display_name || '');
      submitData.append('description', formData.description || '');
      submitData.append('product_url', formData.product_url || '');
      submitData.append('price_cents', formData.price_cents.toString());
      submitData.append('currency', formData.currency);

      // Prepare variants with proper structure
      const variantsWithImages = formData.variants.map(variant => ({
        ...variant,
        images: (variant.images || []).map((img, idx) => ({
          image: img.image,
          is_primary: idx === 0,
          position: idx
        }))
      }));

      // Add arrays as JSON strings
      submitData.append('variants', JSON.stringify(variantsWithImages));

      // Only send the IDs for the selected association type
      if (associationMode === 'players') {
        submitData.append('player_ids', JSON.stringify(formData.player_ids));
        submitData.append('character_ids', JSON.stringify([])); // Empty array for characters
      } else {
        submitData.append('player_ids', JSON.stringify([])); // Empty array for players
        submitData.append('character_ids', JSON.stringify(formData.character_ids));
      }

      submitData.append('category_ids', JSON.stringify(formData.category_ids));

      // Add image as Base64 if selected
      console.log('Image file state:', imageFile);
      console.log('Association mode:', associationMode);
      console.log('Player IDs:', formData.player_ids);
      console.log('Character IDs:', formData.character_ids);
      console.log('Selected players:', selectedPlayers);
      console.log('Selected characters:', selectedCharacters);

      if (imageFile && imageFile instanceof File) {
        console.log('Converting image to Base64:', imageFile.name, 'Size:', imageFile.size, 'Type:', imageFile.type);

        // Convert image to Base64
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onload = (e) => {
            const base64String = e.target.result; // This includes data:image/jpeg;base64,
            console.log('Base64 image length:', base64String.length);
            resolve(base64String);
          };
          reader.readAsDataURL(imageFile);
        });

        const base64Image = await base64Promise;
        submitData.append('image', base64Image);
      } else {
        console.log('No valid image file found. imageFile:', imageFile);
      }

      // Debug: Log what we're sending
      console.log('FormData contents:');
      for (let [key, value] of submitData.entries()) {
        console.log(key, typeof value === 'string' ? value : '[File/Object]');
      }

      const response = await apiClient.post('/products', submitData);

      setSubmitStatus('success');

      // Reset form and return to step 1
      setCurrentStep(1);
      setFormData({
        vendor_id: '',
        sku: '',
        name: '',
        display_name: '',
        description: '',
        product_url: '',
        price_cents: 0,
        currency: 'EUR',
        variants: [{
          sku: '',
          size: '',
          color: '',
          material: '',
          price_cents: 0,
          currency: 'EUR',
          stock: 0,
          images: []
        }],
        player_ids: [],
        character_ids: [],
        category_ids: []
      });
      setSelectedPlayers([]);
      setSelectedCharacters([]);
      setSelectedCategories([]);
      setImageFile(null);
      setImagePreview(null);
      setErrors({});

      // Optional: Clear success message after a few seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);

    } catch (error) {
      console.error('Error creating product:', error);
      setSubmitStatus('error');

      // Handle specific error types
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create product';

      if (errorMessage.includes('duplicate key value violates unique constraint "products_sku_key"')) {
        setErrors({ sku: 'This SKU already exists. Please use a unique SKU.' });
      } else if (errorMessage.includes('UniqueViolation')) {
        setErrors({ general: 'A product with these details already exists. Please check your input.' });
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Entry chooser UI
  if (entryMode === 'choose') {
    return (
      <div className="min-h-screen flex flex-col text-white pt-14 sm:pt-16">
        <Navbar />
        <main className="flex-1">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h1 className="text-2xl font-bold mb-8">Product Management</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                type="button"
                onClick={() => setEntryMode('create')}
                className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-6 text-left"
              >
                <div className="text-lg font-semibold mb-2">Create Product</div>
                <div className="text-sm text-white/70">Start a new product with variants, categories and players.</div>
              </button>
              <button
                type="button"
                onClick={async () => {
                  setAssignOpen(true);
                  setAssignLoading(true);
                  setAssignError('');
                  setAssignItems([]);
                  setAssignFilter('');
                  try {
                    const { data } = await apiClient.get('/products', {
                      params: { limit: 1000, offset: 0 }
                    });
                    setAssignItems(Array.isArray(data?.items) ? data.items : []);
                  } catch (e) {
                    setAssignError('Failed to load products');
                  } finally {
                    setAssignLoading(false);
                  }
                }}
                className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-6 text-left"
              >
                <div className="text-lg font-semibold mb-2">Assign SKU</div>
                <div className="text-sm text-white/70">Select from existing products and assign SKU.</div>
              </button>
              <button
                type="button"
                onClick={async () => {
                  setAssignPlayerOpen(true);
                  setAssignPlayerLoading(true);
                  setAssignPlayerError('');
                  setAssignPlayerItems([]);
                  setAssignPlayerFilter('');
                  setSelectedPlayersForAssign({});
                  try {
                    const { data } = await apiClient.get('/products', {
                      params: { limit: 1000, offset: 0 }
                    });
                    setAssignPlayerItems(Array.isArray(data?.items) ? data.items : []);
                  } catch (e) {
                    setAssignPlayerError('Failed to load products');
                  } finally {
                    setAssignPlayerLoading(false);
                  }
                }}
                className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-6 text-left"
              >
                <div className="text-lg font-semibold mb-2">Assign Player</div>
                <div className="text-sm text-white/70">Assign products to players.</div>
              </button>
              <button
                type="button"
                onClick={async () => {
                  setAssignCharacterOpen(true);
                  setAssignCharacterLoading(true);
                  setAssignCharacterError('');
                  setAssignCharacterItems([]);
                  setAssignCharacterFilter('');
                  setSelectedCharactersForAssign({});
                  try {
                    const { data } = await apiClient.get('/products', {
                      params: { limit: 1000, offset: 0 }
                    });
                    setAssignCharacterItems(Array.isArray(data?.items) ? data.items : []);
                  } catch (e) {
                    setAssignCharacterError('Failed to load products');
                  } finally {
                    setAssignCharacterLoading(false);
                  }
                }}
                className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-6 text-left"
              >
                <div className="text-lg font-semibold mb-2">Assign Character</div>
                <div className="text-sm text-white/70">Assign products to characters.</div>
              </button>
            </div>
          </div>
        </main>
        <Footer />

        {/* Assign Player Modal */}
        {assignPlayerOpen && (
          <div className="fixed inset-0 z-[2147483646] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={() => setAssignPlayerOpen(false)} />
            <div className="relative w-[92vw] max-w-4xl rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a1628]/95 to-[#1a2448]/95 shadow-2xl p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-semibold">Assign Player to Products</div>
                <button type="button" onClick={() => setAssignPlayerOpen(false)} className="text-white/70 hover:text-white">✕</button>
              </div>
              
              {/* Success Message */}
              {assignPlayerSuccess && (
                <div className="mb-3 p-3 rounded-lg bg-green-600/20 border border-green-600/30 text-green-300 flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Success!</div>
                    <div className="text-sm">{assignPlayerSuccess}</div>
                  </div>
                  <button onClick={() => setAssignPlayerSuccess('')} className="text-green-300/70 hover:text-green-300">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              
              {/* Error Message */}
              {assignPlayerErrorMsg && (
                <div className="mb-3 p-3 rounded-lg bg-red-600/20 border border-red-600/30 text-red-300 flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Error</div>
                    <div className="text-sm">{assignPlayerErrorMsg}</div>
                  </div>
                  <button onClick={() => setAssignPlayerErrorMsg('')} className="text-red-300/70 hover:text-red-300">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              
              <div className="mb-3 flex items-center gap-2">
                <input
                  type="text"
                  value={assignPlayerFilter}
                  onChange={(e) => setAssignPlayerFilter(e.target.value)}
                  placeholder="Filter by name, display name, or SKU"
                  className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-sm"
                />
                <div className="text-[11px] text-white/60">{assignPlayerItems.length} items</div>
              </div>
              {assignPlayerLoading && (
                <div className="py-10 flex items-center justify-center"><Loader label="Loading products" /></div>
              )}
              {assignPlayerError && !assignPlayerLoading && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded p-2">{assignPlayerError}</div>
              )}
              {!assignPlayerLoading && !assignPlayerError && (
                <div className="max-h-[60vh] overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-3 pr-1">
                  {assignPlayerItems
                    .filter(it => {
                      const q = assignPlayerFilter.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        String(it.name || '').toLowerCase().includes(q) ||
                        String(it.display_name || '').toLowerCase().includes(q) ||
                        String(it.sku || '').toLowerCase().includes(q)
                      );
                    })
                    .map((it) => (
                      <div key={`${it.id}-${it.sku || 'nosku'}`} className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-3 flex gap-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/10 flex-shrink-0">
                          {it.image_url ? <img src={it.image_url} alt={it.display_name || it.name} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{it.display_name || it.name}</div>
                          <div className="text-xs text-white/70 truncate">SKU: {it.sku || '—'}</div>
                          <div className="mt-2">
                            <SearchMultiSelect
                              options={players}
                              selected={selectedPlayersForAssign[it.id] || []}
                              onChange={(selectedPlayers) => setSelectedPlayersForAssign(prev => ({ ...prev, [it.id]: selectedPlayers }))}
                              placeholder="Select players..."
                              searchKey="name"
                              displayKey="name"
                            />
                            <button
                              type="button"
                              disabled={assigningPlayerId === it.id || !selectedPlayersForAssign[it.id] || selectedPlayersForAssign[it.id].length === 0}
                              className={`mt-2 w-full px-2 py-1 text-[11px] rounded border ${assigningPlayerId === it.id || !selectedPlayersForAssign[it.id] || selectedPlayersForAssign[it.id].length === 0 ? 'border-white/10 bg-white/5 text-white/50' : 'border-emerald-400/40 bg-emerald-600/15 hover:bg-emerald-600/25'}`}
                              title="Assign this product to selected players"
                              onClick={async () => {
                                const selectedPlayers = selectedPlayersForAssign[it.id];
                                if (!selectedPlayers || selectedPlayers.length === 0) return;
                                try {
                                  setAssigningPlayerId(it.id);
                                  setAssignPlayerSuccess('');
                                  setAssignPlayerErrorMsg('');
                                  const playerIds = selectedPlayers.map(p => p.id);
                                  const playerNames = selectedPlayers.map(p => p.name).join(', ');
                                  await apiClient.post('/products/assigned', {
                                    product_id: it.id,
                                    player_ids: playerIds
                                  });
                                  setAssignPlayerSuccess(`Product "${it.display_name || it.name}" successfully assigned to ${playerIds.length} player(s): ${playerNames}`);
                                  // Clear selection after success
                                  setSelectedPlayersForAssign(prev => ({ ...prev, [it.id]: [] }));
                                  // Auto-dismiss after 5 seconds
                                  setTimeout(() => setAssignPlayerSuccess(''), 5000);
                                } catch (e) {
                                  const status = e.response?.status;
                                  const errorMsg = e.response?.data?.message || e.response?.data?.error || 'Failed to assign players';
                                  if (status === 500) {
                                    setAssignPlayerErrorMsg(`Server Error (500): ${errorMsg}. Please try again or contact support if the issue persists.`);
                                  } else if (status === 400) {
                                    setAssignPlayerErrorMsg(`Invalid Request (400): ${errorMsg}`);
                                  } else if (status === 404) {
                                    setAssignPlayerErrorMsg(`Not Found (404): ${errorMsg}`);
                                  } else {
                                    setAssignPlayerErrorMsg(`Error: ${errorMsg}`);
                                  }
                                  // Auto-dismiss error after 8 seconds
                                  setTimeout(() => setAssignPlayerErrorMsg(''), 8000);
                                } finally {
                                  setAssigningPlayerId(null);
                                }
                              }}
                            >
                              {assigningPlayerId === it.id ? 'Assigning…' : 'Assign Players'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  {assignPlayerItems.length === 0 && (
                    <div className="text-xs text-white/60">No items.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assign Character Modal */}
        {assignCharacterOpen && (
          <div className="fixed inset-0 z-[2147483646] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={() => setAssignCharacterOpen(false)} />
            <div className="relative w-[92vw] max-w-4xl rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a1628]/95 to-[#1a2448]/95 shadow-2xl p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-semibold">Assign Character to Products</div>
                <button type="button" onClick={() => setAssignCharacterOpen(false)} className="text-white/70 hover:text-white">✕</button>
              </div>
              
              {/* Success Message */}
              {assignCharacterSuccess && (
                <div className="mb-3 p-3 rounded-lg bg-green-600/20 border border-green-600/30 text-green-300 flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Success!</div>
                    <div className="text-sm">{assignCharacterSuccess}</div>
                  </div>
                  <button onClick={() => setAssignCharacterSuccess('')} className="text-green-300/70 hover:text-green-300">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              
              {/* Error Message */}
              {assignCharacterErrorMsg && (
                <div className="mb-3 p-3 rounded-lg bg-red-600/20 border border-red-600/30 text-red-300 flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Error</div>
                    <div className="text-sm">{assignCharacterErrorMsg}</div>
                  </div>
                  <button onClick={() => setAssignCharacterErrorMsg('')} className="text-red-300/70 hover:text-red-300">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              
              <div className="mb-3 flex items-center gap-2">
                <input
                  type="text"
                  value={assignCharacterFilter}
                  onChange={(e) => setAssignCharacterFilter(e.target.value)}
                  placeholder="Filter by name, display name, or SKU"
                  className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-sm"
                />
                <div className="text-[11px] text-white/60">{assignCharacterItems.length} items</div>
              </div>
              {assignCharacterLoading && (
                <div className="py-10 flex items-center justify-center"><Loader label="Loading products" /></div>
              )}
              {assignCharacterError && !assignCharacterLoading && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded p-2">{assignCharacterError}</div>
              )}
              {!assignCharacterLoading && !assignCharacterError && (
                <div className="max-h-[60vh] overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-3 pr-1">
                  {assignCharacterItems
                    .filter(it => {
                      const q = assignCharacterFilter.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        String(it.name || '').toLowerCase().includes(q) ||
                        String(it.display_name || '').toLowerCase().includes(q) ||
                        String(it.sku || '').toLowerCase().includes(q)
                      );
                    })
                    .map((it) => (
                      <div key={`${it.id}-${it.sku || 'nosku'}`} className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-3 flex gap-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/10 flex-shrink-0">
                          {it.image_url ? <img src={it.image_url} alt={it.display_name || it.name} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{it.display_name || it.name}</div>
                          <div className="text-xs text-white/70 truncate">SKU: {it.sku || '—'}</div>
                          <div className="mt-2">
                            <SearchMultiSelect
                              options={characters}
                              selected={selectedCharactersForAssign[it.id] || []}
                              onChange={(selectedCharacters) => setSelectedCharactersForAssign(prev => ({ ...prev, [it.id]: selectedCharacters }))}
                              placeholder="Select characters..."
                              searchKey="name"
                              displayKey="name"
                            />
                            <button
                              type="button"
                              disabled={assigningCharacterId === it.id || !selectedCharactersForAssign[it.id] || selectedCharactersForAssign[it.id].length === 0}
                              className={`mt-2 w-full px-2 py-1 text-[11px] rounded border ${assigningCharacterId === it.id || !selectedCharactersForAssign[it.id] || selectedCharactersForAssign[it.id].length === 0 ? 'border-white/10 bg-white/5 text-white/50' : 'border-emerald-400/40 bg-emerald-600/15 hover:bg-emerald-600/25'}`}
                              title="Assign this product to selected characters"
                              onClick={async () => {
                                const selectedCharacters = selectedCharactersForAssign[it.id];
                                if (!selectedCharacters || selectedCharacters.length === 0) return;
                                try {
                                  setAssigningCharacterId(it.id);
                                  setAssignCharacterSuccess('');
                                  setAssignCharacterErrorMsg('');
                                  const characterIds = selectedCharacters.map(c => c.id);
                                  const characterNames = selectedCharacters.map(c => c.name).join(', ');
                                  await apiClient.post('/products/assigned', {
                                    product_id: it.id,
                                    character_ids: characterIds
                                  });
                                  setAssignCharacterSuccess(`Product "${it.display_name || it.name}" successfully assigned to ${characterIds.length} character(s): ${characterNames}`);
                                  // Clear selection after success
                                  setSelectedCharactersForAssign(prev => ({ ...prev, [it.id]: [] }));
                                  // Auto-dismiss after 5 seconds
                                  setTimeout(() => setAssignCharacterSuccess(''), 5000);
                                } catch (e) {
                                  const status = e.response?.status;
                                  const errorMsg = e.response?.data?.message || e.response?.data?.error || 'Failed to assign characters';
                                  if (status === 500) {
                                    setAssignCharacterErrorMsg(`Server Error (500): ${errorMsg}. Please try again or contact support if the issue persists.`);
                                  } else if (status === 400) {
                                    setAssignCharacterErrorMsg(`Invalid Request (400): ${errorMsg}`);
                                  } else if (status === 404) {
                                    setAssignCharacterErrorMsg(`Not Found (404): ${errorMsg}`);
                                  } else {
                                    setAssignCharacterErrorMsg(`Error: ${errorMsg}`);
                                  }
                                  // Auto-dismiss error after 8 seconds
                                  setTimeout(() => setAssignCharacterErrorMsg(''), 8000);
                                } finally {
                                  setAssigningCharacterId(null);
                                }
                              }}
                            >
                              {assigningCharacterId === it.id ? 'Assigning…' : 'Assign Characters'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  {assignCharacterItems.length === 0 && (
                    <div className="text-xs text-white/60">No items.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assign SKU Modal (same as in create mode) */}
        {assignOpen && (
          <div className="fixed inset-0 z-[2147483646] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={() => setAssignOpen(false)} />
            <div className="relative w-[92vw] max-w-3xl rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a1628]/95 to-[#1a2448]/95 shadow-2xl p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-semibold">Assign SKU from Category 13</div>
                <button type="button" onClick={() => setAssignOpen(false)} className="text-white/70 hover:text-white">✕</button>
              </div>
              <div className="mb-3 flex items-center gap-2">
                <input
                  type="text"
                  value={assignFilter}
                  onChange={(e) => setAssignFilter(e.target.value)}
                  placeholder="Filter by name, display name, or SKU"
                  className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-sm"
                />
                <div className="text-[11px] text-white/60">{assignItems.length} items</div>
              </div>
              {assignLoading && (
                <div className="py-10 flex items-center justify-center"><Loader label="Loading products-simple" /></div>
              )}
              {assignError && !assignLoading && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded p-2">{assignError}</div>
              )}
              {!assignLoading && !assignError && (
                <div className="max-h-[60vh] overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-3 pr-1">
                  {assignItems
                    .filter(it => {
                      const q = assignFilter.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        String(it.name || '').toLowerCase().includes(q) ||
                        String(it.display_name || '').toLowerCase().includes(q) ||
                        String(it.sku || '').toLowerCase().includes(q)
                      );
                    })
                    .map((it) => (
                      <div key={`${it.id}-${it.sku || 'nosku'}`} className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-3 flex gap-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/10 flex-shrink-0">
                          {it.image_url ? <img src={it.image_url} alt={it.display_name || it.name} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{it.display_name || it.name}</div>
                          <div className="text-xs text-white/70 truncate">SKU: {it.sku || '—'}</div>
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              type="text"
                              value={skuEdits[it.id] ?? it.sku ?? ''}
                              onChange={(e) => setSkuEdits((m) => ({ ...m, [it.id]: e.target.value }))}
                              placeholder="Enter new SKU"
                              className="flex-1 px-2 py-1 rounded bg-white/5 border border-white/15 text-[12px]"
                            />
                            <button
                              type="button"
                              disabled={savingProductId === it.id || (skuEdits[it.id] ?? it.sku ?? '') === (it.sku || '')}
                              className={`px-2 py-1 text-[11px] rounded border ${savingProductId === it.id ? 'border-white/10 bg-white/5 text-white/50' : 'border-emerald-400/40 bg-emerald-600/15 hover:bg-emerald-600/25'}`}
                              title="Update this product's SKU"
                              onClick={async () => {
                                const nextSku = (skuEdits[it.id] ?? it.sku ?? '').trim();
                                if (!nextSku) return;
                                try {
                                  setSavingProductId(it.id);
                                  await apiClient.put(`/products/${it.id}`, { sku: nextSku });
                                  setAssignItems(prev => prev.map(x => x.id === it.id ? { ...x, sku: nextSku } : x));
                                } catch (e) {
                                  alert('Failed to update SKU');
                                } finally {
                                  setSavingProductId(null);
                                }
                              }}
                            >
                              {savingProductId === it.id ? 'Saving…' : 'Update SKU'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  {assignItems.length === 0 && (
                    <div className="text-xs text-white/60">No items.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader label="Loading form data..." />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="mb-4 p-4 rounded-lg bg-red-600/20 border border-red-600/30 text-red-300">
              <div className="flex items-center justify-center mb-2">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Failed to load form data
              </div>
              <p className="text-sm">Please refresh the page or contact support if the problem persists.</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Navbar />

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Create New Product</h1>
            <p className="text-white/70">Follow the steps to add a new product with all details</p>
          </div>
          <button
            onClick={() => window.location.href = '/productupdate'}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
          >
            Manage Products
          </button>
        </div>

        {/* Step Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${step === currentStep
                  ? 'border-cyan-500 bg-cyan-500 text-white'
                  : step < currentStep
                    ? 'border-green-500 bg-green-500 text-white'
                    : 'border-white/30 text-white/50'
                  }`}>
                  {step < currentStep ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step
                  )}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className={`text-sm font-medium ${step === currentStep ? 'text-cyan-300' : step < currentStep ? 'text-green-300' : 'text-white/50'}`}>
                    Step {step}
                  </p>
                  <p className={`text-xs ${step === currentStep ? 'text-white' : step < currentStep ? 'text-white/80' : 'text-white/40'}`}>
                    {getStepTitle(step)}
                  </p>
                </div>
                {step < totalSteps && (
                  <div className={`hidden sm:block w-16 h-0.5 ml-4 ${step < currentStep ? 'bg-green-500' : 'bg-white/20'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <div className="mb-6 p-4 rounded-lg bg-green-600/20 border border-green-600/30 text-green-300">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Product created successfully!
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mb-6 p-4 rounded-lg bg-red-600/20 border border-red-600/30 text-red-300">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {errors.general || 'Failed to create product. Please try again.'}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Step Content */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              {getStepTitle(currentStep)}
            </h2>

            {/* Step 1: Product Details & Image */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Vendor */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Vendor <span className="text-red-400">*</span>
                    </label>
                    <SearchSelect
                      options={vendors}
                      value={formData.vendor_id}
                      onChange={(v) => updateFormData('vendor_id', v)}
                      placeholder="Select vendor"
                      displayKey="name"
                      valueKey="id"
                    />
                    {errors.vendor_id && <p className="text-red-400 text-sm mt-1">{errors.vendor_id}</p>}
                  </div>

                  {/* SKU */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      SKU <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) => updateFormData('sku', e.target.value)}
                      placeholder="JERSEY-PSG-2024"
                      className={`w-full px-3 py-2 rounded-lg bg-white/5 border ${errors.sku ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                    {errors.sku && <p className="text-red-400 text-sm mt-1">{errors.sku}</p>}
                  </div>

                  {/* Product Name */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Product Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                      placeholder="PSG Jersey 2024"
                      className={`w-full px-3 py-2 rounded-lg bg-white/5 border ${errors.name ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                    {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                  </div>

                  {/* Display Name */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Display Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => updateFormData('display_name', e.target.value)}
                      placeholder="Paris Saint-Germain Home Jersey 2024"
                      className={`w-full px-3 py-2 rounded-lg bg-white/5 border ${errors.display_name ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                    {errors.display_name && <p className="text-red-400 text-sm mt-1">{errors.display_name}</p>}
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Price <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70">€</span>
                      <input
                        type="text"
                        value={formatPrice(formData.price_cents).replace('€', '')}
                        onChange={(e) => handlePriceChange(e.target.value)}
                        placeholder="85.00"
                        className={`w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border ${errors.price_cents ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      />
                    </div>
                    {errors.price_cents && <p className="text-red-400 text-sm mt-1">{errors.price_cents}</p>}
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Currency <span className="text-red-400">*</span>
                    </label>
                    <SearchSelect
                      options={[{ id: 'EUR', name: 'EUR' }, { id: 'USD', name: 'USD' }, { id: 'GBP', name: 'GBP' }]}
                      value={formData.currency}
                      onChange={(v) => updateFormData('currency', v)}
                      placeholder="Select currency"
                      displayKey="name"
                      valueKey="id"
                    />
                    {errors.currency && <p className="text-red-400 text-sm mt-1">{errors.currency}</p>}
                  </div>

                  {/* Product URL */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-white mb-2">Product URL</label>
                    <input
                      type="url"
                      value={formData.product_url}
                      onChange={(e) => updateFormData('product_url', e.target.value)}
                      placeholder="https://example.com/product"
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-white mb-2">
                      Description <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => updateFormData('description', e.target.value)}
                      placeholder="Product description..."
                      rows={4}
                      className={`w-full px-3 py-2 rounded-lg bg-white/5 border ${errors.description ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none`}
                    />
                    {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Product Image</h3>
                  <ImageUpload
                    onImageChange={handleImageChange}
                    preview={imagePreview}
                    error={errors.image}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Associate (Players or Characters) & Categories */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Association Mode Toggle */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-white/80">Associate with:</label>
                  <div className="inline-flex rounded-lg overflow-hidden border border-white/10">
                    <button
                      type="button"
                      onClick={() => setAssociationMode('players')}
                      className={`px-3 py-1.5 text-sm ${associationMode === 'players' ? 'bg-cyan-600 text-white' : 'bg-white/5 text-white/80 hover:bg-white/10'}`}
                    >Players</button>
                    <button
                      type="button"
                      onClick={() => setAssociationMode('characters')}
                      className={`px-3 py-1.5 text-sm ${associationMode === 'characters' ? 'bg-cyan-600 text-white' : 'bg-white/5 text-white/80 hover:bg-white/10'}`}
                    >Characters</button>
                  </div>
                </div>

                {/* Players or Characters Section */}
                {associationMode === 'players' ? (
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Associated Players
                    </h3>
                    <SearchMultiSelect
                      options={players}
                      selected={selectedPlayers}
                      onChange={handlePlayersChange}
                      placeholder="Select players..."
                      searchKey="name"
                      displayKey="name"
                    />
                  </div>
                ) : (
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Associated Characters
                    </h3>
                    <SearchMultiSelect
                      options={characters}
                      selected={selectedCharacters}
                      onChange={handleCharactersChange}
                      placeholder="Select characters..."
                      searchKey="name"
                      displayKey="name"
                    />
                  </div>
                )}

                {/* Categories Section */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Categories
                  </h3>
                  <CategoryMultiSelect
                    categories={categories}
                    selected={selectedCategories}
                    onChange={handleCategoriesChange}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Variants */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Product Variants
                  </h3>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Variant
                  </button>
                </div>

                {errors.variants && <p className="text-red-400 text-sm mb-4">{errors.variants}</p>}

                <div className="space-y-4">
                  {formData.variants.map((variant, index) => (
                    <div key={index} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-medium text-white">Variant {index + 1}</h4>
                        {formData.variants.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Size */}
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Size <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={variant.size}
                            onChange={(e) => updateVariant(index, 'size', e.target.value)}
                            onBlur={() => {
                              if (variant.size && variant.size.includes(',')) {
                                const split = splitVariantBySizes(variant, formData.sku, formData.variants);
                                if (split && split.length > 1) {
                                  // Replace this variant with the split ones
                                  setFormData(prev => {
                                    const newVariants = [
                                      ...prev.variants.slice(0, index),
                                      ...split,
                                      ...prev.variants.slice(index + 1)
                                    ];
                                    return { ...prev, variants: newVariants };
                                  });
                                }
                              }
                            }}
                            placeholder="S, M, L, XL"
                            className={`w-full px-3 py-2 rounded-lg bg-white/5 border ${errors[`variant_${index}_size`] ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                          />
                          {errors[`variant_${index}_size`] && <p className="text-red-400 text-sm mt-1">{errors[`variant_${index}_size`]}</p>}
                        </div>

                        {/* Color */}
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Color <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={variant.color}
                            onChange={(e) => updateVariant(index, 'color', e.target.value)}
                            placeholder="Blue, Red, White"
                            className={`w-full px-3 py-2 rounded-lg bg-white/5 border ${errors[`variant_${index}_color`] ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                          />
                          {errors[`variant_${index}_color`] && <p className="text-red-400 text-sm mt-1">{errors[`variant_${index}_color`]}</p>}
                        </div>

                        {/* Material */}
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Material <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={variant.material}
                            onChange={(e) => updateVariant(index, 'material', e.target.value)}
                            placeholder="Polyester, Cotton"
                            className={`w-full px-3 py-2 rounded-lg bg-white/5 border ${errors[`variant_${index}_material`] ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                          />
                          {errors[`variant_${index}_material`] && <p className="text-red-400 text-sm mt-1">{errors[`variant_${index}_material`]}</p>}
                        </div>

                        {/* Price */}
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">Price</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70">€</span>
                            <input
                              type="text"
                              value={formatPrice(variant.price_cents).replace('€', '')}
                              onChange={(e) => handlePriceChange(e.target.value, true, index)}
                              placeholder="85.00"
                              className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        {/* Stock */}
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Stock <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="number"
                            value={variant.stock}
                            onChange={(e) => updateVariant(index, 'stock', parseInt(e.target.value) || 0)}
                            placeholder="25"
                            min="0"
                            className={`w-full px-3 py-2 rounded-lg bg-white/5 border ${errors[`variant_${index}_stock`] ? 'border-red-400' : 'border-white/10'} text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                          />
                          {errors[`variant_${index}_stock`] && <p className="text-red-400 text-sm mt-1">{errors[`variant_${index}_stock`]}</p>}
                        </div>

                        {/* Auto-generated SKU */}
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">Generated SKU</label>
                          <input
                            type="text"
                            value={variant.sku || generateVariantSku(formData.sku, variant.size, variant.color)}
                            readOnly
                            className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white/70 cursor-not-allowed"
                          />
                        </div>
                      </div>

                      {/* Variant Images Section (auto metadata, thumbnails, delete on hover) */}
                      <div className="mt-6">
                        <h5 className="text-white font-semibold mb-2 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a4 4 0 004 4h10a4 4 0 004-4V7a4 4 0 00-4-4H7a4 4 0 00-4 4z" />
                          </svg>
                          Variant Images
                        </h5>
                        <div className="flex items-center gap-4 flex-wrap">
                          {(variant.images || []).map((img, imgIdx) => (
                            <div key={imgIdx} className="relative group">
                              <img
                                src={typeof img.image === 'string' && img.image.startsWith('data:') ? img.image : (img.preview || '')}
                                alt={imgIdx === 0 ? 'Primary image' : `Image ${imgIdx + 1}`}
                                className={`w-20 h-20 object-cover rounded border-2 ${imgIdx === 0 ? 'border-cyan-400' : 'border-white/20'} transition-all`}
                              />
                              {imgIdx === 0 && (
                                <span className="absolute top-1 left-1 bg-cyan-600 text-white text-xs px-2 py-0.5 rounded font-bold shadow">Primary</span>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  let newImages = (variant.images || []).filter((_, i) => i !== imgIdx);
                                  // Always set first image as primary and update positions
                                  newImages = newImages.map((img, idx) => ({
                                    ...img,
                                    is_primary: idx === 0,
                                    position: idx
                                  }));
                                  updateVariant(index, 'images', newImages);
                                }}
                                className="absolute inset-0 flex items-center justify-center bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded"
                                style={{ pointerEvents: 'auto' }}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                          {/* Upload Button, only if less than 10 images */}
                          {(variant.images?.length || 0) < 10 && (
                            <label className="w-20 h-20 flex items-center justify-center bg-white/10 border-2 border-dashed border-white/20 rounded cursor-pointer hover:bg-cyan-700/20 transition-all">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                  if ((variant.images?.length || 0) >= 10) return; // Prevent more than 10
                                  const file = e.target.files[0];
                                  if (file) {
                                    // Client-side size guard: 50KB max
                                    if (file.size > 50 * 1024) {
                                      setErrors(prev => ({ ...prev, [`variant_${index}_image`]: 'Image too large. Max size is 50KB' }));
                                      e.target.value = '';
                                      return;
                                    }
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                      const base64 = reader.result;
                                      const newImages = [...(variant.images || []), {
                                        image: base64,
                                        preview: base64,
                                        alt_text: '',
                                        view_type: 'front',
                                        is_primary: false,
                                        position: (variant.images?.length || 0)
                                      }];
                                      // Always set first image as primary
                                      if (newImages.length === 1) {
                                        newImages[0].is_primary = true;
                                        newImages[0].position = 0;
                                      } else {
                                        newImages.forEach((img, idx) => {
                                          img.is_primary = idx === 0;
                                          img.position = idx;
                                        });
                                      }
                                      updateVariant(index, 'images', newImages);
                                      // Clear any previous image error for this variant
                                      setErrors(prev => ({ ...prev, [`variant_${index}_image`]: null }));
                                    };
                                    reader.readAsDataURL(file);
                                    e.target.value = '';
                                  }
                                }}
                              />
                              <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </label>
                          )}
                          {(variant.images?.length || 0) >= 10 && (
                            <span className="text-xs text-red-400 ml-2">Maximum 10 images allowed per variant.</span>
                          )}
                          {errors[`variant_${index}_image`] && (
                            <div className="text-xs text-red-400 mt-2">{errors[`variant_${index}_image`]}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-medium rounded-lg transition-colors flex items-center"
              >
                Next
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:from-gray-600 disabled:to-gray-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Product...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Product
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </main>

      <Footer />

      {/* Assign SKU Modal */}
      {assignOpen && (
        <div className="fixed inset-0 z-[2147483646] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setAssignOpen(false)} />
          <div className="relative w-[92vw] max-w-3xl rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a1628]/95 to-[#1a2448]/95 shadow-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-semibold">Assign SKU from Category 13</div>
              <button type="button" onClick={() => setAssignOpen(false)} className="text-white/70 hover:text-white">✕</button>
            </div>
            <div className="mb-3 flex items-center gap-2">
              <input
                type="text"
                value={assignFilter}
                onChange={(e) => setAssignFilter(e.target.value)}
                placeholder="Filter by name, display name, or SKU"
                className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-sm"
              />
              <div className="text-[11px] text-white/60">{assignItems.length} items</div>
            </div>
            {assignLoading && (
              <div className="py-10 flex items-center justify-center"><Loader label="Loading products-simple" /></div>
            )}
            {assignError && !assignLoading && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded p-2">{assignError}</div>
            )}
            {!assignLoading && !assignError && (
              <div className="max-h-[60vh] overflow-auto grid grid-cols-1 sm:grid-cols-2 gap-3 pr-1">
                {assignItems
                  .filter(it => {
                    const q = assignFilter.trim().toLowerCase();
                    if (!q) return true;
                    return (
                      String(it.name || '').toLowerCase().includes(q) ||
                      String(it.display_name || '').toLowerCase().includes(q) ||
                      String(it.sku || '').toLowerCase().includes(q)
                    );
                  })
                  .map((it) => (
                    <div key={`${it.id}-${it.sku || 'nosku'}`} className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-3 flex gap-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/10 flex-shrink-0">
                        {it.image_url ? <img src={it.image_url} alt={it.display_name || it.name} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{it.display_name || it.name}</div>
                        <div className="text-xs text-white/70 truncate">SKU: {it.sku || '—'}</div>
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            type="text"
                            value={skuEdits[it.id] ?? it.sku ?? ''}
                            onChange={(e) => setSkuEdits((m) => ({ ...m, [it.id]: e.target.value }))}
                            placeholder="Enter new SKU"
                            className="flex-1 px-2 py-1 rounded bg-white/5 border border-white/15 text-[12px]"
                          />
                          <button
                            type="button"
                            disabled={savingProductId === it.id || (skuEdits[it.id] ?? it.sku ?? '') === (it.sku || '')}
                            className={`px-2 py-1 text-[11px] rounded border ${savingProductId === it.id ? 'border-white/10 bg-white/5 text-white/50' : 'border-emerald-400/40 bg-emerald-600/15 hover:bg-emerald-600/25'}`}
                            title="Update this product's SKU"
                            onClick={async () => {
                              const nextSku = (skuEdits[it.id] ?? it.sku ?? '').trim();
                              if (!nextSku) return;
                              try {
                                setSavingProductId(it.id);
                                await apiClient.put(`/products/${it.id}`, { sku: nextSku });
                                setAssignItems(prev => prev.map(x => x.id === it.id ? { ...x, sku: nextSku } : x));
                              } catch (e) {
                                alert('Failed to update SKU');
                              } finally {
                                setSavingProductId(null);
                              }
                            }}
                          >
                            {savingProductId === it.id ? 'Saving…' : 'Update SKU'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                {assignItems.length === 0 && (
                  <div className="text-xs text-white/60">No items.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
